import { weekdayShort } from "@repo/client/format";
import { "profile-editors.floating" as floatingText } from "@repo/i18n";
import { getLocale } from "@repo/i18n/runtime";
import { cx } from "../../../lib/cx";
import { usePlan } from "../../plan/use-plan";
import { dayName } from "./options";
import {
  DISPLAY_WEEK,
  LETTER_WEEK,
  planSplit,
  splitFor,
  type WeekShape,
} from "./routine-helpers";

export interface PlanWeek {
  /** A custom week's day labels, in order; empty for any other shape. */
  readonly custom: readonly string[];
  readonly shape: WeekShape;
}

/**
 * The shape of the plan in effect, from the plan query every screen shares:
 * the split it was BUILT as (`D213`), so a trainer's PPL + arms or custom
 * week previews as itself. Until the plan loads, and with no plan, the
 * default protocol's.
 */
export function usePlanWeek(): PlanWeek {
  const plan = usePlan().data;
  const shape = planSplit(plan);
  return {
    custom:
      shape === "custom"
        ? (plan?.sessions.map((session) => session.label) ?? [])
        : [],
    shape,
  };
}

/** Saturday and Sunday (`0` Sunday). */
function isWeekend(day: number): boolean {
  return day === 0 || day === 6;
}

export interface WeekStripProps {
  compact?: boolean;
  /** Fixed weekdays, `0` Sunday. */
  fixed: readonly number[];
  /** How many sessions float with no weekday. */
  flex: number;
  /** The week to draw, when it is not the plan's: the editor's draft split. */
  shape?: WeekShape;
}

/**
 * Seven columns, Sunday first (`D218`): a lettered disc on a fixed day, a
 * quiet dash on a rest day, the floating sessions in a dashed tray below
 * (A2 routine.tsx:32-102). The letters still go out Monday first
 * ({@link LETTER_WEEK}). Saturday and Sunday sit on a light tint, to be
 * found at a glance. T51's routine section draws it too.
 */
export function WeekStrip({ fixed, flex, compact, shape }: WeekStripProps) {
  const locale = getLocale();
  const days = [...new Set(fixed)].sort(
    (a, b) => LETTER_WEEK.indexOf(a) - LETTER_WEEK.indexOf(b)
  );
  const week = usePlanWeek();
  const split = splitFor(days.length + flex, shape ?? week.shape, week.custom);
  const floating = split.slice(days.length);
  const disc = compact === true ? "size-9 text-base" : "size-10 text-lg";
  return (
    <div>
      <ol className="grid grid-cols-7 gap-1">
        {DISPLAY_WEEK.map((day) => {
          const at = days.indexOf(day);
          const workout = at >= 0 ? split[at] : undefined;
          return (
            <li
              className={cx(
                "flex flex-col items-center gap-1.5 rounded-2xl py-1.5",
                isWeekend(day) && "bg-canvas"
              )}
              data-weekend={isWeekend(day) ? "" : undefined}
              key={day}
            >
              <span
                className={cx(
                  "text-xs",
                  workout ? "font-semibold text-ink" : "text-ink-3"
                )}
              >
                {weekdayShort(day, locale)}
              </span>
              {workout ? (
                <span
                  className={cx(
                    "a-display grid place-items-center rounded-full bg-inverse text-on-inverse",
                    disc
                  )}
                >
                  {workout.letter}
                </span>
              ) : (
                <span
                  className={cx(
                    "grid place-items-center rounded-full bg-surface-2",
                    compact === true ? "size-9" : "size-10"
                  )}
                >
                  <span className="h-0.5 w-2.5 rounded-full bg-line-strong" />
                </span>
              )}
            </li>
          );
        })}
      </ol>
      {floating.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border-[1.5px] border-line-strong border-dashed px-3 py-2.5">
          <span className="text-ink-2 text-sm">
            {floatingText({ count: floating.length })}
          </span>
          {floating.map((w) => (
            <span
              className="inline-flex h-7 items-center gap-1.5 rounded-full bg-surface-2 pr-3 pl-1 font-medium text-ink text-sm"
              key={w.letter}
            >
              <span className="a-display grid size-5 place-items-center rounded-full bg-inverse text-on-inverse text-xs">
                {w.letter}
              </span>
              {dayName(w)}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
